import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LogoutService } from 'app/core/auth/logout.service';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface RolePayload {
    name: string;
}

interface Role {
    id: string;
    name: string;
}

interface Permission {
    id: string;
    name: string;
}

interface PermissionPayload {
    permissions: string[];
}

@Component({
    selector: 'app-role',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './role.component.html',
    styleUrl: './role.component.scss',
})
export class RoleComponent {
    isDropdownOpen = false;
    isLoggingOut = false;
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;
    isPermissionModalOpen = false; // Modal untuk set permission

    Role: Role[] = [];
    Permission: Permission[] = []; // Permission untuk halaman saat ini
    allPermissions: Permission[] = []; // Semua permission (tanpa pagination)
    selectedPermissions: string[] = []; // Permission yang dipilih
    currentRoleForPermission: Role | null = null; // Role yang sedang di-set permission

    roleForm: RolePayload = {
        name: '',
    };

    selectedRoleId: string = '';
    currentPermissionPage = 1;
    totalPermissionPages = 1;

    constructor(
        private logoutService: LogoutService,
        private apiService: ApiService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.getRole();
        this.getPermission(); // Load permission saat init
    }

    // ==================== CRUD OPERATIONS ====================

    // GET - List Roles
    async getRole(): Promise<void> {
        try {
            const response = await this.apiService.get(
                '/api/v1/manage/role/list'
            );
            this.Role = response.data.list;
        } catch (error) {
            console.error('Error fetching roles:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memuat data role',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    async getPermission(page: number = 1): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/manage/permission/list?page=${page}&per_page=10`
            );

            this.Permission = response.data.list;
            this.currentPermissionPage = response.data.pagination.current_page;
            this.totalPermissionPages = response.data.pagination.total_page;
        } catch (error) {
            console.error('Error fetching permissions:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Terjadi kesalahan saat memuat data permission',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // GET - All Permissions (tanpa pagination, untuk select all)
    async getAllPermissions(): Promise<void> {
        try {
            const response = await this.apiService.get(
                '/api/v1/manage/permission/list?per_page=1000' // Ambil semua data
            );

            this.allPermissions = response.data.list;
        } catch (error) {
            console.error('Error fetching all permissions:', error);
            this.allPermissions = [];
        }
    }
    // GET - Single Role (untuk edit)
    async getRoleById(roleId: string): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/manage/role/${roleId}`
            );
            // console.log(response);

            this.roleForm = {
                name: response.data.name,
            };

            this.selectedRoleId = roleId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            console.error('Error fetching role:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Gagal memuat data role',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // GET - Role Permissions (untuk mendapatkan permission yang sudah di-assign)
    async getRolePermissions(roleId: string): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/manage/role/${roleId}`
            );

            // Asumsikan response memiliki field permissions yang berisi array permission names
            // Sesuaikan dengan struktur response API Anda
            if (response.data && response.data.permissions) {
                this.selectedPermissions = response.data.permissions.map(
                    (p: any) => p.name || p
                );
            } else {
                this.selectedPermissions = [];
            }
        } catch (error) {
            console.error('Error fetching role permissions:', error);
            this.selectedPermissions = [];
        }
    }

    // POST - Create Role
    async createRole(): Promise<void> {
        if (!this.validateForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/manage/role', this.roleForm);

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Role berhasil ditambahkan',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getRole();
        } catch (error: any) {
            console.error('Error creating role:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal menambahkan role',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // PUT/PATCH - Update Role
    async updateRole(): Promise<void> {
        if (!this.validateForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(
                `/api/v1/manage/role/${this.selectedRoleId}`,
                this.roleForm
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Role berhasil diupdate',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getRole();
        } catch (error: any) {
            console.error('Error updating role:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal mengupdate role',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // DELETE - Delete Role
    async deleteRole(roleId: string, roleName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus role "${roleName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            try {
                await this.apiService.delete('/api/v1/manage/role', roleId);

                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Role berhasil dihapus',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false,
                });

                this.getRole();
            } catch (error: any) {
                console.error('Error deleting role:', error);
                await Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error?.error?.message || 'Gagal menghapus role',
                    confirmButtonColor: '#dc3545',
                });
            }
        }
    }

    // ==================== PERMISSION OPERATIONS ====================

    // Open modal untuk set permission
    async openPermissionModal(role: Role): Promise<void> {
        this.currentRoleForPermission = role;
        await this.getAllPermissions(); // Ambil semua permission untuk select all
        await this.getPermission(1); // Ambil page 1 untuk ditampilkan
        await this.getRolePermissions(role.id);
        this.isPermissionModalOpen = true;
    }

    // Close modal permission
    closePermissionModal(): void {
        this.isPermissionModalOpen = false;
        this.currentRoleForPermission = null;
        this.selectedPermissions = [];
    }

    // Toggle checkbox permission
    togglePermission(permissionName: string): void {
        const index = this.selectedPermissions.indexOf(permissionName);
        if (index > -1) {
            // Jika sudah ada, hapus (uncheck)
            this.selectedPermissions.splice(index, 1);
        } else {
            // Jika belum ada, tambahkan (check)
            this.selectedPermissions.push(permissionName);
        }
    }

    // Check apakah permission sudah dipilih
    isPermissionSelected(permissionName: string): boolean {
        return this.selectedPermissions.includes(permissionName);
    }

    // Select All Permissions (dari semua halaman)
    selectAllPermissions(): void {
        this.selectedPermissions = this.allPermissions.map((p) => p.name);
    }

    // Deselect All Permissions
    deselectAllPermissions(): void {
        this.selectedPermissions = [];
    }

    nextPermissionPage() {
        if (this.currentPermissionPage < this.totalPermissionPages) {
            this.getPermission(this.currentPermissionPage + 1);
        }
    }

    prevPermissionPage() {
        if (this.currentPermissionPage > 1) {
            this.getPermission(this.currentPermissionPage - 1);
        }
    }

    // Submit/Update permissions untuk role
    async updateRolePermissions(): Promise<void> {
        if (!this.currentRoleForPermission) {
            return;
        }

        this.isSubmitting = true;

        const payload: PermissionPayload = {
            permissions: this.selectedPermissions,
        };

        try {
            await this.apiService.post(
                `/api/v1/manage/role/${this.currentRoleForPermission.id}/permission`,
                payload
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Permission berhasil diupdate',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closePermissionModal();
        } catch (error: any) {
            console.error('Error updating permissions:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal mengupdate permission',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // ==================== MODAL OPERATIONS ====================

    openAddModal(): void {
        this.isEditMode = false;
        this.resetForm();
        this.isModalOpen = true;
    }

    openEditModal(roleId: string): void {
        this.getRoleById(roleId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.roleForm = {
            name: '',
        };
        this.selectedRoleId = '';
    }

    // ==================== FORM VALIDATION ====================

    validateForm(): boolean {
        if (!this.roleForm.name || this.roleForm.name.trim() === '') {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Nama role wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        if (this.roleForm.name.trim().length < 3) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Nama role minimal 3 karakter',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        return true;
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateRole();
        } else {
            await this.createRole();
        }
    }
}
